import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/atoms";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { TripSettingsSection } from "@/redux/slices/onboarding/types";
import { SetupPanel, SetupPanelItem, WhyDoINeedModal, WalkthroughModal, ReadSetupGuideModal } from "../common";
import { quickSetupContent, quickSetupExplanations } from "../data/quickSetupContent";
import { isSectionComplete } from "../helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../hooks";

const TripSettingsPaths = {
  TRIP_COST: "/config/trip/trip-cost",
  TIME_DAY_MULTIPLIER: "/config/trip/time-and-day",
  HOURLY_HIRE: "/config/trip/hourly-daily-hire",
  TRANSFER_AND_VEHICLE_OPTIONS: "/config/trip/vehicle-categories",
  UNSCHEDULED_STOPS: "/config/trip/unscheduled-stops",
  CANCELLATION_FEES: "/config/trip/cancellation-fees",
  BILLING_SETTINGS: "/config/trip/billing-settings",
  WAITING_TIMES: "/config/trip/waiting-times",
  DISPATCH_SETTINGS: "/config/trip/dispatch-settings",
} as const;

interface TripSettingsPanelProps {
  defaultOpen?: boolean;
}

enum ModalType {
  TRIP_COST_WHY = "trip-cost-why",
  TRIP_COST_WALKTHROUGH = "trip-cost-walkthrough",
  TRIP_COST_DOCUMENTATION = "trip-cost-documentation",
  TIME_AND_DAY_WHY = "time-and-day-why",
  TIME_AND_DAY_WALKTHROUGH = "time-and-day-walkthrough",
  TIME_AND_DAY_DOCUMENTATION = "time-and-day-documentation",
  HOURLY_HIRE_WHY = "hourly-hire-why",
  HOURLY_HIRE_WALKTHROUGH = "hourly-hire-walkthrough",
  HOURLY_HIRE_DOCUMENTATION = "hourly-hire-documentation",
  VEHICLE_OPTIONS_WHY = "vehicle-options-why",
  VEHICLE_OPTIONS_WALKTHROUGH = "vehicle-options-walkthrough",
  VEHICLE_OPTIONS_DOCUMENTATION = "vehicle-options-documentation",
  UNSCHEDULED_STOPS_WHY = "unscheduled-stops-why",
  UNSCHEDULED_STOPS_WALKTHROUGH = "unscheduled-stops-walkthrough",
  UNSCHEDULED_STOPS_DOCUMENTATION = "unscheduled-stops-documentation",
  CANCELLATION_FEES_WHY = "cancellation-fees-why",
  CANCELLATION_FEES_WALKTHROUGH = "cancellation-fees-walkthrough",
  CANCELLATION_FEES_DOCUMENTATION = "cancellation-fees-documentation",
  BILLING_SETTINGS_WHY = "billing-settings-why",
  BILLING_SETTINGS_WALKTHROUGH = "billing-settings-walkthrough",
  BILLING_SETTINGS_DOCUMENTATION = "billing-settings-documentation",
  WAITING_TIMES_WHY = "waiting-times-why",
  WAITING_TIMES_WALKTHROUGH = "waiting-times-walkthrough",
  WAITING_TIMES_DOCUMENTATION = "waiting-times-documentation",
  DISPATCH_SETTINGS_WHY = "dispatch-settings-why",
  DISPATCH_SETTINGS_WALKTHROUGH = "dispatch-settings-walkthrough",
  DISPATCH_SETTINGS_DOCUMENTATION = "dispatch-settings-documentation",
}

export const TripSettingsPanel = ({ defaultOpen = false }: TripSettingsPanelProps) => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isTripCostCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.tripCost) : false;
  const isTimeDayCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.timeDayMultiplier) : false;
  const isHourlyHireCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.hourlyHire) : false;
  const isVehicleOptionsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.transferAndVehicleOptions) : false;
  const isUnscheduledStopsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.unscheduledStops) : false;
  const isCancellationFeesCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.cancellationFees) : false;
  const isBillingSettingsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.billingSettings) : false;
  const isWaitingTimesCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.waitingTimes) : false;
  const isDispatchSettingsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.tripSettings.dispatchSettings) : false;

  const completedSteps = [
    isTripCostCompleted,
    isTimeDayCompleted,
    isHourlyHireCompleted,
    isVehicleOptionsCompleted,
    isUnscheduledStopsCompleted,
    isCancellationFeesCompleted,
    isBillingSettingsCompleted,
    isWaitingTimesCompleted,
    isDispatchSettingsCompleted,
  ].filter(Boolean).length;
  const totalSteps = 9;

  const handleModalClose = () => setActiveModal(null);

  const handleNavigateAndUpdate = (path: string, section: TripSettingsSection) => {
    setQuickSetupFlag(section);
    navigate(path);

    if (onboardingStatus) {
      const sectionMap = {
        [TripSettingsSection.TRIP_COST]: onboardingStatus.tripSettings.tripCost,
        [TripSettingsSection.TIME_DAY_MULTIPLIER]: onboardingStatus.tripSettings.timeDayMultiplier,
        [TripSettingsSection.HOURLY_HIRE]: onboardingStatus.tripSettings.hourlyHire,
        [TripSettingsSection.TRANSFER_AND_VEHICLE_OPTIONS]: onboardingStatus.tripSettings.transferAndVehicleOptions,
        [TripSettingsSection.UNSCHEDULED_STOPS]: onboardingStatus.tripSettings.unscheduledStops,
        [TripSettingsSection.CANCELLATION_FEES]: onboardingStatus.tripSettings.cancellationFees,
        [TripSettingsSection.BILLING_SETTINGS]: onboardingStatus.tripSettings.billingSettings,
        [TripSettingsSection.WAITING_TIMES]: onboardingStatus.tripSettings.waitingTimes,
        [TripSettingsSection.DISPATCH_SETTINGS]: onboardingStatus.tripSettings.dispatchSettings,
      };

      const sectionStatus = sectionMap[section];

      if (sectionStatus && !isSectionComplete(sectionStatus)) {
        updateSectionStatus({ section, completed: true }).catch((error) => {
          console.error("Failed to update section status:", error);
        });
      }
    }
  };

  const tripCostFeaturedIcon = {
    name: "DollarCircle",
    background: "bg-primary-light",
  };

  const timeAndDayFeaturedIcon = {
    name: "Timer1",
    background: "bg-primary-light",
  };

  const hourlyHireFeaturedIcon = {
    name: "Timer1",
    background: "bg-primary-light",
  };

  const vehicleOptionsFeaturedIcon = {
    name: "Car",
    background: "bg-primary-light",
  };

  const unscheduledStopsFeaturedIcon = {
    name: "Location",
    background: "bg-primary-light",
  };

  const cancellationFeesFeaturedIcon = {
    name: "Forbidden",
    background: "bg-primary-light",
  };

  const billingSettingsFeaturedIcon = {
    name: "Setting5",
    background: "bg-primary-light",
  };

  const waitingTimesFeaturedIcon = {
    name: "Clock",
    background: "bg-primary-light",
  };

  const dispatchSettingsFeaturedIcon = {
    name: "CalendarTick",
    background: "bg-primary-light",
  };

  return (
    <>
      <SetupPanel
        stepNumber={3}
        title="Trip Settings"
        description="Set how your trips are priced, booked, and managed—all in one place"
        stepCount={totalSteps}
        completedSteps={completedSteps}
        defaultOpen={defaultOpen}
      >
        <SetupPanelItem
          icon="DollarCircle"
          iconBackground="bg-primary-light"
          title="Trip Cost"
          isCompleted={isTripCostCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.TRIP_COST, TripSettingsSection.TRIP_COST),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.TRIP_COST_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.TRIP_COST_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.TRIP_COST_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="Timer1"
          iconBackground="bg-primary-light"
          title="Time & Day (Multiplier)"
          isCompleted={isTimeDayCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.TIME_DAY_MULTIPLIER, TripSettingsSection.TIME_DAY_MULTIPLIER),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.TIME_AND_DAY_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.TIME_AND_DAY_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.TIME_AND_DAY_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="Timer1"
          iconBackground="bg-primary-light"
          title="Hourly - Daily Hire"
          isCompleted={isHourlyHireCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.HOURLY_HIRE, TripSettingsSection.HOURLY_HIRE),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.HOURLY_HIRE_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.HOURLY_HIRE_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.HOURLY_HIRE_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="Car"
          iconBackground="bg-primary-light"
          title="Transfer & Vehicle Options"
          isCompleted={isVehicleOptionsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.TRANSFER_AND_VEHICLE_OPTIONS, TripSettingsSection.TRANSFER_AND_VEHICLE_OPTIONS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.VEHICLE_OPTIONS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.VEHICLE_OPTIONS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.VEHICLE_OPTIONS_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="Location"
          iconBackground="bg-primary-light"
          title="Unscheduled Stops"
          isCompleted={isUnscheduledStopsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.UNSCHEDULED_STOPS, TripSettingsSection.UNSCHEDULED_STOPS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.UNSCHEDULED_STOPS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.UNSCHEDULED_STOPS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.UNSCHEDULED_STOPS_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="Forbidden"
          iconBackground="bg-primary-light"
          title="Cancellation Fees"
          isCompleted={isCancellationFeesCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.CANCELLATION_FEES, TripSettingsSection.CANCELLATION_FEES),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.CANCELLATION_FEES_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.CANCELLATION_FEES_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.CANCELLATION_FEES_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="Setting5"
          iconBackground="bg-primary-light"
          title="Billing Settings"
          isCompleted={isBillingSettingsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.BILLING_SETTINGS, TripSettingsSection.BILLING_SETTINGS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.BILLING_SETTINGS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.BILLING_SETTINGS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.BILLING_SETTINGS_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="Clock"
          iconBackground="bg-primary-light"
          title="Waiting Times"
          isCompleted={isWaitingTimesCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.WAITING_TIMES, TripSettingsSection.WAITING_TIMES),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.WAITING_TIMES_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.WAITING_TIMES_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.WAITING_TIMES_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon="CalendarTick"
          iconBackground="bg-primary-light"
          title="Dispatch Settings"
          isCompleted={isDispatchSettingsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleNavigateAndUpdate(TripSettingsPaths.DISPATCH_SETTINGS, TripSettingsSection.DISPATCH_SETTINGS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.DISPATCH_SETTINGS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.DISPATCH_SETTINGS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.DISPATCH_SETTINGS_DOCUMENTATION) },
          ]}
        />
      </SetupPanel>

      {/* Trip Cost Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.TRIP_COST_WHY}
        onClose={handleModalClose}
        title="Why Set Up Trip Cost?"
        featuredIcon={tripCostFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.tripCost}
      />

      <WalkthroughModal
        open={activeModal === ModalType.TRIP_COST_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Trip Cost Setup)"
        featuredIcon={tripCostFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.tripCost.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.TRIP_COST_DOCUMENTATION}
        onClose={handleModalClose}
        title="Trip Cost Setup Guide"
        featuredIcon={tripCostFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.tripCost.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.tripCost.pdfUrl}
      />

      {/* Time & Day Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.TIME_AND_DAY_WHY}
        onClose={handleModalClose}
        title="Why Configure Time & Day Multipliers?"
        featuredIcon={timeAndDayFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.timeAndDay}
      />

      <WalkthroughModal
        open={activeModal === ModalType.TIME_AND_DAY_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Time & Day Multiplier Setup)"
        featuredIcon={timeAndDayFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.timeAndDay.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.TIME_AND_DAY_DOCUMENTATION}
        onClose={handleModalClose}
        title="Time & Day Documentation"
        featuredIcon={timeAndDayFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.timeAndDay.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.timeAndDay.pdfUrl}
      />

      {/* Hourly Hire Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.HOURLY_HIRE_WHY}
        onClose={handleModalClose}
        title="Why Set Up Hourly Hire?"
        featuredIcon={hourlyHireFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.hourlyHire}
      />

      <WalkthroughModal
        open={activeModal === ModalType.HOURLY_HIRE_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Hourly Hire Setup)"
        featuredIcon={hourlyHireFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.hourlyHire.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.HOURLY_HIRE_DOCUMENTATION}
        onClose={handleModalClose}
        title="Hourly Hire Documentation"
        featuredIcon={hourlyHireFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.hourlyHire.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.hourlyHire.pdfUrl}
      />

      {/* Vehicle Options Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.VEHICLE_OPTIONS_WHY}
        onClose={handleModalClose}
        title="Why Configure Vehicle Options?"
        featuredIcon={vehicleOptionsFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.vehicleOptions}
      />

      <WalkthroughModal
        open={activeModal === ModalType.VEHICLE_OPTIONS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Vehicle Options Setup)"
        featuredIcon={vehicleOptionsFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.vehicleOptions.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.VEHICLE_OPTIONS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Vehicle Options Documentation"
        featuredIcon={vehicleOptionsFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.vehicleOptions.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.vehicleOptions.pdfUrl}
      />

      {/* Unscheduled Stops Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.UNSCHEDULED_STOPS_WHY}
        onClose={handleModalClose}
        title="Why Configure Unscheduled Stops?"
        featuredIcon={unscheduledStopsFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.unscheduledStops}
      />

      <WalkthroughModal
        open={activeModal === ModalType.UNSCHEDULED_STOPS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Unscheduled Stops Setup)"
        featuredIcon={unscheduledStopsFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.unscheduledStops.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.UNSCHEDULED_STOPS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Unscheduled Stops Documentation"
        featuredIcon={unscheduledStopsFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.unscheduledStops.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.unscheduledStops.pdfUrl}
      />

      {/* Cancellation Fees Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.CANCELLATION_FEES_WHY}
        onClose={handleModalClose}
        title="Why Configure Cancellation Fees?"
        featuredIcon={cancellationFeesFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.cancellationFees}
      />

      <WalkthroughModal
        open={activeModal === ModalType.CANCELLATION_FEES_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Cancellation Fees Setup)"
        featuredIcon={cancellationFeesFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.cancellationFees.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.CANCELLATION_FEES_DOCUMENTATION}
        onClose={handleModalClose}
        title="Cancellation Fees Documentation"
        featuredIcon={cancellationFeesFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.cancellationFees.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.cancellationFees.pdfUrl}
      />

      {/* Billing Settings Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.BILLING_SETTINGS_WHY}
        onClose={handleModalClose}
        title="Why Configure Billing Settings?"
        featuredIcon={billingSettingsFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.billingSettings}
      />

      <WalkthroughModal
        open={activeModal === ModalType.BILLING_SETTINGS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Billing Settings Setup)"
        featuredIcon={billingSettingsFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.billingSettings.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.BILLING_SETTINGS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Billing Settings Documentation"
        featuredIcon={billingSettingsFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.billingSettings.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.billingSettings.pdfUrl}
      />

      {/* Waiting Times Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.WAITING_TIMES_WHY}
        onClose={handleModalClose}
        title="Why Configure Waiting Times?"
        featuredIcon={waitingTimesFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.waitingTimes}
      />

      <WalkthroughModal
        open={activeModal === ModalType.WAITING_TIMES_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Waiting Times Setup)"
        featuredIcon={waitingTimesFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.waitingTimes.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.WAITING_TIMES_DOCUMENTATION}
        onClose={handleModalClose}
        title="Waiting Times Documentation"
        featuredIcon={waitingTimesFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.waitingTimes.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.waitingTimes.pdfUrl}
      />

      {/* Dispatch Settings Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.DISPATCH_SETTINGS_WHY}
        onClose={handleModalClose}
        title="Why Configure Dispatch Settings?"
        featuredIcon={dispatchSettingsFeaturedIcon}
        explanations={quickSetupExplanations.tripSettings.dispatchSettings}
      />

      <WalkthroughModal
        open={activeModal === ModalType.DISPATCH_SETTINGS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Dispatch Settings Setup)"
        featuredIcon={dispatchSettingsFeaturedIcon}
        videoUrl={quickSetupContent.tripSettings.dispatchSettings.videoUrl}
      />

      <ReadSetupGuideModal
        open={activeModal === ModalType.DISPATCH_SETTINGS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Dispatch Settings Documentation"
        featuredIcon={dispatchSettingsFeaturedIcon}
        documentationUrl={quickSetupContent.tripSettings.dispatchSettings.documentationUrl}
        pdfUrl={quickSetupContent.tripSettings.dispatchSettings.pdfUrl}
      />
    </>
  );
};
